import { useCallback, useEffect, useState } from 'react';
import { Building2, Users, Handshake, RefreshCw } from 'lucide-react';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import Tabs from '../components/Tabs';
import OpenInAppButton from '../components/OpenInAppButton';
import AppViewTabs from '../components/AppViewTabs';
import { secondaryButtonClass } from '../components/FilterBar';
import { titleCase } from '../lib/format';
import {
  fetchCrmCompanies,
  fetchCrmContacts,
  fetchCrmDeals,
  fetchIntegrationStatus,
  type CrmListResponse,
  type RawIntegrationStatus,
} from '../lib/api';

type Row = Record<string, unknown>;
type TabId = 'companies' | 'contacts' | 'deals';

const PRIORITY_KEYS = [
  'name',
  'displayName',
  'title',
  'firstName',
  'lastName',
  'email',
  'primaryEmail',
  'domainName',
  'domain',
  'stage',
  'amount',
  'value',
  'phone',
  'city',
  'status',
  'createdAt',
];

function coerceRows(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[];
  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(value)) return value as Row[];
    }
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (value && typeof value === 'object') {
        for (const inner of Object.values(value as Record<string, unknown>)) {
          if (Array.isArray(inner)) return inner as Row[];
        }
      }
    }
  }
  return [];
}

function scalarValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['firstName', 'name', 'label', 'primaryEmail', 'value', 'amountMicros']) {
      if (typeof obj[key] === 'string' || typeof obj[key] === 'number') {
        return String(obj[key]);
      }
    }
    return null;
  }
  return String(value);
}

function buildColumns(rows: Row[]): Column<Row>[] {
  const keys = new Set<string>();
  for (const row of rows.slice(0, 10)) {
    for (const [key, value] of Object.entries(row)) {
      if (scalarValue(value) !== null && key !== 'id') keys.add(key);
    }
  }
  const ordered = [
    ...PRIORITY_KEYS.filter((k) => keys.has(k)),
    ...[...keys].filter((k) => !PRIORITY_KEYS.includes(k)),
  ].slice(0, 6);

  if (ordered.length === 0) {
    return [{ key: 'id', header: 'ID', render: (r) => scalarValue(r.id) ?? '—' }];
  }

  return ordered.map((key) => ({
    key,
    header: titleCase(key),
    render: (r: Row) => scalarValue(r[key]) ?? '—',
  }));
}

function Crm() {
  const [tab, setTab] = useState<TabId>('companies');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<RawIntegrationStatus | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [statusResult, dataResult] = await Promise.all([
      fetchIntegrationStatus('twenty'),
      (async () => {
        const fetcher: Record<TabId, () => Promise<{ ok: boolean; data?: CrmListResponse }>> = {
          companies: () => fetchCrmCompanies(50),
          contacts: () => fetchCrmContacts(50),
          deals: () => fetchCrmDeals(50),
        };
        return fetcher[tab]();
      })(),
    ]);
    setIntegration(statusResult.ok ? statusResult.data ?? null : null);
    if (dataResult.ok && dataResult.data) {
      setRows(coerceRows(dataResult.data.data));
    } else {
      setRows([]);
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const icons: Record<TabId, typeof Building2> = {
    companies: Building2,
    contacts: Users,
    deals: Handshake,
  };
  const EmptyIcon = icons[tab];
  const crmConnected = integration?.connected === true;
  const emptyMessage = crmConnected
    ? `No ${tab} in Twenty CRM yet.`
    : integration?.configured
      ? 'Twenty CRM is configured but not connected. Check Admin → Twenty CRM or create an API key at crm.zerotail.org.'
      : 'Connect Twenty CRM to populate this list.';

  const dashboard = (
    <>
      <div className="flex items-center justify-between">
        <Tabs
          tabs={[
            { id: 'companies', label: 'Companies' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'deals', label: 'Deals' },
          ]}
          active={tab}
          onChange={(id) => setTab(id as TabId)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <OpenInAppButton integrationKey="twenty" />
          <button type="button" onClick={load} className={secondaryButtonClass}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      <DataTable
        columns={buildColumns(rows)}
        rows={rows}
        rowKey={(r, i) => scalarValue(r.id) ?? String(i)}
        loading={loading}
        emptyTitle={`No ${tab}`}
        emptyMessage={emptyMessage}
        emptyIcon={<EmptyIcon size={32} />}
      />
    </>
  );

  return (
    <PageShell
      title="CRM / Clients"
      subtitle="Companies, contacts, and deals synced from Twenty CRM."
    >
      <AppViewTabs appId="twenty" embedTitle="Twenty CRM" dashboard={dashboard} />
    </PageShell>
  );
}

export default Crm;
